import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Breadcrumb,
  Button,
  Space,
  Row,
  Popconfirm,
  Tooltip,
} from "antd";
import { ReactComponent as Editicon } from "../../../image/Editicon.svg";
import { ReactComponent as Invoice } from "../../../image/Invoice.svg";
import PlusSquareOutlined from "../../../image/PlusSquareOutlined.svg";
import { ReactComponent as Archive } from "../../../image/Archive.svg";
import { ReactComponent as Archiveicon } from "../../../image/Archiveicon.svg";
import SearchIcon from "../../../image/SearchIcon.svg";
import { authUserData } from "../../../utils/Helper";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import { debounce } from "lodash";
import { getRequest, postRequest } from "../../../axios";
import "antd/dist/antd.min.css";
import "../../../styles/Main.scss";
import ButtonField from "../../../common/htmlComponents/ButtonField";

const { Search } = Input;
const PerformaInvoiceList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [performaInvoiceList, setPerformaInvoiceList] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [reload, setReload] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10
  });

  useEffect(() => {
    const getPerformaInvoiceList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest("performa-invoice/", state);

        if (apiResponse.status) {
          setPerformaInvoiceList(apiResponse.data.results);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_performa_invoice_list"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    getPerformaInvoiceList();
  }, [state, reload, t]);

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";
    if (sortField === "invoice_number" && sortOrder === "ascend") {
      sortingEnumKey = "invoice_number";
    }
    if (sortField === "invoice_number" && sortOrder === "descend") {
      sortingEnumKey = "-invoice_number";
    }
    if (sortField === "business_name" && sortOrder === "ascend") {
      sortingEnumKey = "company__name";
    }
    if (sortField === "business_name" && sortOrder === "descend") {
      sortingEnumKey = "-company__name";
    }
    if (sortField === "subscription_name" && sortOrder === "ascend") {
      sortingEnumKey = "subscription__package_name";
    }
    if (sortField === "subscription_name" && sortOrder === "descend") {
      sortingEnumKey = "-subscription__package_name";
    }

    if (sortField === "invoice_date" && sortOrder === "ascend") {
      sortingEnumKey = "invoice_date";
    }
    if (sortField === "invoice_date" && sortOrder === "descend") {
      sortingEnumKey = "-invoice_date";
    }

    if (sortField === "due_date" && sortOrder === "ascend") {
      sortingEnumKey = "due_date";
    }
    if (sortField === "due_date" && sortOrder === "descend") {
      sortingEnumKey = "-due_date";
    }
    if (sortField === "currency_name" && sortOrder === "ascend") {
      sortingEnumKey = "currency_name";
    }
    if (sortField === "currency_name" && sortOrder === "descend") {
      sortingEnumKey = "-currency_name";
    }
    if (sortField === "quantity" && sortOrder === "ascend") {
      sortingEnumKey = "quantity";
    }
    if (sortField === "quantity" && sortOrder === "descend") {
      sortingEnumKey = "-quantity";
    }

    if (sortField === "sell_price" && sortOrder === "ascend") {
      sortingEnumKey = "sell_price";
    }
    if (sortField === "sell_price" && sortOrder === "descend") {
      sortingEnumKey = "-sell_price";
    }
    if (sortField === "gst_rate" && sortOrder === "ascend") {
      sortingEnumKey = "gst_rate";
    }
    if (sortField === "gst_rate" && sortOrder === "descend") {
      sortingEnumKey = "-gst_rate";
    }

    return sortingEnumKey;
  };

  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };

  const onSearch = (value) => {
      if (value) {
          setState({
              ...state,
              search: value,
          });
      } else {
          const { search, ...newState } = state; // Remove 'search' key from state
          setState(newState);
      }
  };

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRow(selectedRowKeys);
    },
  };

  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);

  const handleArchive = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("performa-invoice-delete/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        SuccessNotificationMsg("Performa Invoice archive successfully");
        setReload(!reload);
        setSelectedRow([]);
      } else {
        ErrorNotificationMsg("Error in delete Performa Invoice");
      }
    } catch (error) {
      // ErrorNotificationMsg(error);
    }
  };

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      key: "invoice_number",
      sorter: true,
    },
    {
      title: "Client",
      dataIndex: "business_name",
      key: "business_name",
      sorter: true,
    },
    {
      title: "Subscription",
      dataIndex: "subscription_name",
      key: "subscription_name",
      sorter: true,
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      key: "invoice_date",
      sorter: true,
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      sorter: true,
    },
    {
      title: "Currency",
      dataIndex: "currency_name",
      key: "currency_name",
      sorter: true,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      sorter: true,
    },
    {
      title: "Sell Price",
      dataIndex: "sell_price",
      key: "sell_price",
      sorter: true,
    },
    {
      title: "GST Rate",
      dataIndex: "gst_rate",
      key: "gst_rate",
      sorter: true,
    },

    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 150,
      render: (text, record) => (
        <Space>
          <Link
            to={{
              pathname: "/performa-invoice/edit/" + btoa(record.id),
            }}
          >
            {authUserData()?.permission.includes(
              "subcription|change_performainvoice"
            ) && <Editicon />}
          </Link>

          <Popconfirm
            className="action"
            title={t("pop_title")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            onConfirm={() => handleArchive(record.id)}
          >
            {authUserData()?.permission.includes(
              "subcription|delete_performainvoice"
            ) && <Archive style={{ cursor: "pointer" }} />}
          </Popconfirm>
          <Invoice />
          {/* <span>Invoice</span> */}
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip
              color={colors}
              placement="right"
              title={
                <span style={{ color: "#fff" }}>
                  Enhance transparency, efficiency, and trust in your transactions with our Proforma Invoice feature.
                </span>
              }
            >
              <h3>{t("performaInvoiceList")}</h3>
            </Tooltip>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("performaInvoiceList")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            {" "}
            <div className="searchwrp">
              <Space direction="vertical">
              <Search
  value={searchText}
  onChange={(e) => {
    const value = e.target.value;
    setSearchText(value);
    debounceLoadData(value, onSearch);
  }}
  allowClear
  onSearch={(value) => {
    setSearchText(value);
    onSearch(value);
  }}
  prefix={
    <img
      src={SearchIcon}
      alt="SearchIcon"
      className="SearchIcon"
      width={22}
    />
  }
  placeholder={t("pl_search_list")}
  style={{ width: 250 }}
/>
              </Space>
            </div>
            {authUserData()?.permission.includes(
              "subcription|add_performainvoice"
            ) && (
                <Button
                  className="BlueShadowBtn"
                  type="primary"
                  onClick={() => navigate("/performa-invoice/add")}
                  icon={<img src={PlusSquareOutlined} alt="Plus Square" />}
                >
                  {t("addPerformaInvoice")}
                </Button>
              )}
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleArchive()}
              >
                {authUserData()?.permission.includes(
                  "subcription|delete_performainvoice"
                ) && (
                    <ButtonField
                      className="BlueShadowBtn"
                      type="primary"
                      htmlType="button"
                      label={t("pl_archive")}
                    />
                  )}
              </Popconfirm>
            )}
            <Tooltip placement="topLeft" title="Archived Records">
              <Link to="/performa-invoice/archive">
                <Archiveicon />
              </Link>
            </Tooltip>
          </Space>
          {/* {authUserData()?.permission.includes(
          "subcription|add_performainvoice"
        ) && (
          <Button
            className="BlueShadowBtn"
            type="primary"
            onClick={() => navigate("/performa-invoice/add")}
            icon={<img src={PlusSquareOutlined} alt="Plus Square" />}
          >
            {t("addPerformaInvoice")}
          </Button>
        )} */}
        </div>
      </div>
      <section className="grid-sec mt-5">
        <div className="PR-body">
          <Row className="tblheader">
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-heading">
            <h1>{t("performaInvoiceList")}</h1>
          </Col> */}
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-action">
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleArchive()}
              >
                {authUserData()?.permission.includes(
                  "subcription|delete_performainvoice"
                ) &&
                  <ButtonField
                  className="BlueShadowBtn"
                    type="primary"
                    htmlType="button"
                    label={t("pl_archive")}
                  />
                }
               
              </Popconfirm>
            )}
            <Tooltip placement="topLeft" title="Archived Records">
              <Link to="/performa-invoice/archive">
                <Archiveicon />
              </Link>
            </Tooltip>
          </Col> */}
          </Row>


          <Table
            scroll={{
              x: 2000,
            }}
            rowSelection={rowSelection}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={performaInvoiceList}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showTotal: (total) => `Total ${total} items`,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
          />
        </div>
      </section>
    </>
  );
};

export default PerformaInvoiceList;
